import { useEffect, useRef, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useConversationStore } from '../ConversationStoreProvider';
import { replaceStylesheetGlobals, stylesheetDefault } from '../portal/theme/portalTheme';
import { useTokens } from '../tokens/TokensProvider';
import usePageAdditionalDataRequest from './usePageAdditionalDataRequest';
const mapSystemPrimaryButtonsToSuggestedPromptsStyle = (primaryButtons) => {
    if (!primaryButtons || typeof primaryButtons !== 'object') {
        return {};
    }
    return {
        button: {
            color: primaryButtons.font?.color,
            backgroundColor: primaryButtons.background?.color,
            borderColor: primaryButtons.border?.color,
            borderRadius: primaryButtons.border?.radius,
            borderWidth: primaryButtons.border?.thickness,
            fontSize: primaryButtons.font?.fontSize,
            hover: {
                color: primaryButtons.font?.hover?.color,
                backgroundColor: primaryButtons.background?.hover?.color,
                borderColor: primaryButtons.border?.hover?.color,
            },
        },
    };
};
const resolveStyleTokens = (styles, stylesheet) => {
    if (!styles || typeof styles !== 'object')
        return styles;
    const resolved = Array.isArray(styles) ? [] : {};
    Object.entries(styles).forEach(([key, value]) => {
        if (typeof value === 'string') {
            resolved[key] = replaceStylesheetGlobals(stylesheet, value);
        }
        else if (typeof value === 'object' && value !== null) {
            resolved[key] = resolveStyleTokens(value, stylesheet);
        }
        else {
            resolved[key] = value;
        }
    });
    return resolved;
};
const resolveHeaderConfig = (headerConfig) => {
    if (!headerConfig || typeof headerConfig !== 'object')
        return headerConfig;
    return Object.prototype.hasOwnProperty.call(headerConfig, 'header')
        ? headerConfig.header
        : headerConfig;
};
const PageContentDispatcher = () => {
    const store = useConversationStore();
    const tokens = useTokens();
    // The dispatcher is mounted for the whole life of the widget, while <Conversation /> only mounts
    // once the shopper expands the assistant. Requesting from here keeps host-page Product Answers
    // independent of whether the popup is ever opened.
    usePageAdditionalDataRequest(store);
    const suggestedPrompts = useMemo(() => store.suggestedPrompts || [], [store.suggestedPrompts]);
    const smartSummary = useMemo(() => store.smartSummary, [store.smartSummary]);
    const isLoading = store.isLoadingSuggestedPrompts || false;
    const dispatchedRef = useRef(false);
    useEffect(() => {
        const hasPrompts = Array.isArray(suggestedPrompts) && suggestedPrompts.length > 0;
        const hasSummary = smartSummary
            && smartSummary.header
            && Array.isArray(smartSummary.bullets)
            && smartSummary.bullets.length > 0;
        if (!isLoading && (hasPrompts || hasSummary) && !dispatchedRef.current) {
            let cancelled = false;
            const dispatchPageContent = () => {
                const { clientAIConfig, stylesheet, customerContext } = store;
                const isCollection = customerContext?.currentPage?.isCollection;
                const details = {
                    blockType: isCollection ? 'category' : 'product',
                };
                if (hasPrompts) {
                    let header = '';
                    let stylesRaw = {};
                    let customCss = '';
                    if (isCollection) {
                        header = tokens.resolvePortalText(clientAIConfig?.categoryPageSuggestedPromptsHeader, 'categoryPromptHeader') || tokens.resolvePortalText(clientAIConfig?.pageSuggestedPromptsHeader, 'pageSuggestedPromptsHeader') || tokens.resolvePortalText(null, 'categoryPromptHeader');
                    }
                    else {
                        header = tokens.resolvePortalText(clientAIConfig?.pageSuggestedPromptsHeader, 'pageSuggestedPromptsHeader') || tokens.resolvePortalText(null, 'productPromptHeader');
                    }
                    const styleSource = isCollection
                        ? (clientAIConfig?.categoryPageSuggestedPromptsStyle
                            ?? clientAIConfig?.pageSuggestedPromptsStyle)
                        : clientAIConfig?.pageSuggestedPromptsStyle;
                    stylesRaw = (styleSource || {});
                    const cssSource = isCollection
                        ? (clientAIConfig?.categoryPageSuggestedPromptsCustomCss
                            ?? clientAIConfig?.pageSuggestedPromptsCustomCss)
                        : clientAIConfig?.pageSuggestedPromptsCustomCss;
                    customCss = cssSource ?? '';
                    const hasCustomStyles = stylesRaw && Object.keys(stylesRaw).length > 0;
                    if (!hasCustomStyles) {
                        const primaryButtons = stylesheet?.site?.system?.buttons?.primary
                            || stylesheetDefault?.site?.system?.buttons?.primary;
                        stylesRaw = mapSystemPrimaryButtonsToSuggestedPromptsStyle(primaryButtons);
                    }
                    const sharedHeaderRaw = clientAIConfig?.pageGeneratedContentHeader;
                    const sharedHeader = resolveHeaderConfig(sharedHeaderRaw);
                    const stylesRawWithHeader = {
                        ...stylesRaw,
                        ...(sharedHeader ? { header: sharedHeader } : {}),
                    };
                    const styles = resolveStyleTokens(stylesRawWithHeader, stylesheet);
                    const scrollStyleSource = isCollection
                        ? (clientAIConfig?.categoryPageSuggestedPromptsScrollStyle
                            ?? clientAIConfig?.pageSuggestedPromptsScrollStyle)
                        : clientAIConfig?.pageSuggestedPromptsScrollStyle;
                    const scrollStyleRaw = (scrollStyleSource || {});
                    // Deep-merge button/container so scroll-only overrides
                    // (e.g. button.maxWidth) don't wipe base colors.
                    const baseBtn = stylesRaw?.button && typeof stylesRaw.button === 'object'
                        ? stylesRaw.button
                        : {};
                    const scrollBtn = scrollStyleRaw?.button && typeof scrollStyleRaw.button === 'object'
                        ? scrollStyleRaw.button
                        : {};
                    const baseContainer = stylesRaw?.container && typeof stylesRaw.container === 'object'
                        ? stylesRaw.container
                        : {};
                    const scrollContainer = scrollStyleRaw?.container && typeof scrollStyleRaw.container === 'object'
                        ? scrollStyleRaw.container
                        : {};
                    const scrollRest = { ...scrollStyleRaw };
                    delete scrollRest.button;
                    delete scrollRest.container;
                    const stylesScrollRaw = {
                        ...stylesRaw,
                        ...scrollRest,
                        button: {
                            ...baseBtn,
                            ...scrollBtn,
                            hover: { ...baseBtn?.hover, ...scrollBtn?.hover },
                        },
                        container: { ...baseContainer, ...scrollContainer },
                    };
                    const stylesScrollRawWithHeader = {
                        ...stylesScrollRaw,
                        ...(sharedHeader ? { header: sharedHeader } : {}),
                    };
                    const stylesScroll = resolveStyleTokens(stylesScrollRawWithHeader, stylesheet);
                    const inputEnabled = isCollection
                        ? !!(clientAIConfig?.categoryAnswersInputEnabled
                            ?? clientAIConfig?.hostPageSuggestedPromptsInputEnabled)
                        : !!(clientAIConfig?.productAnswersInputEnabled
                            ?? clientAIConfig?.hostPageSuggestedPromptsInputEnabled);
                    const inputPlaceholder = isCollection
                        ? (clientAIConfig?.categoryAnswersInputPlaceholder ?? 'Ask me anything...')
                        : (clientAIConfig?.productAnswersInputPlaceholder ?? 'Ask me anything...');
                    const headerImageUrl = isCollection
                        ? (clientAIConfig?.categoryAnswersHeaderImageUrl
                            ?? clientAIConfig?.productAnswersHeaderImageUrl)
                        : clientAIConfig?.productAnswersHeaderImageUrl;
                    const headerImageAlt = isCollection
                        ? (clientAIConfig?.categoryAnswersHeaderImageAlt
                            ?? clientAIConfig?.productAnswersHeaderImageAlt)
                        : clientAIConfig?.productAnswersHeaderImageAlt;
                    const headerImageAlignmentRaw = isCollection
                        ? (clientAIConfig?.categoryAnswersHeaderImageAlignment
                            ?? clientAIConfig?.productAnswersHeaderImageAlignment)
                        : clientAIConfig?.productAnswersHeaderImageAlignment;
                    const headerImageAlignment = (headerImageAlignmentRaw === 'center'
                        || headerImageAlignmentRaw === 'right')
                        ? headerImageAlignmentRaw
                        : 'left';
                    details.suggestedPrompts = {
                        prompts: [...suggestedPrompts],
                        header,
                        headerImageUrl,
                        headerImageAlt,
                        headerImageAlignment,
                        styles,
                        stylesScroll,
                        customCss,
                        inputEnabled,
                        inputPlaceholder,
                    };
                }
                if (hasSummary) {
                    const headerTokenKey = isCollection ? 'categorySmartSummaryHeader' : 'smartSummaryHeader';
                    let header = tokens.getValue(headerTokenKey);
                    if ((header.startsWith('{{') && header.endsWith('}}')) || !header?.trim()) {
                        header = smartSummary?.header || (isCollection ? 'Key Collection Highlights' : 'Key Product Features');
                    }
                    const generatedByAiLabel = clientAIConfig?.pageSmartSummaryGeneratedByAiLabel
                        ? tokens.resolvePortalText(null, 'generatedByAiLabel')
                        : '';
                    const suggestedStyles = clientAIConfig?.pageSuggestedPromptsStyle || {};
                    const smartSummaryStyles = clientAIConfig?.pageSmartSummaryStyle || {};
                    const sharedHeaderRaw = clientAIConfig?.pageGeneratedContentHeader;
                    const sharedHeader = resolveHeaderConfig(sharedHeaderRaw);
                    const headerStyle = sharedHeader;
                    const summaryStyle = smartSummaryStyles?.summary;
                    const containerStyle = smartSummaryStyles?.container;
                    const stylesRawWithHeader = {
                        ...suggestedStyles,
                        ...(headerStyle ? { header: headerStyle } : {}),
                        summary: summaryStyle,
                        container: containerStyle,
                    };
                    const styles = resolveStyleTokens(stylesRawWithHeader, stylesheet);
                    const customCss = clientAIConfig?.pageSmartSummaryCustomCss || '';
                    const generatedByAiLabelAlignment = (() => {
                        const v = clientAIConfig?.pageSmartSummaryGeneratedByAiLabelAlignment;
                        return v === 'left' || v === 'center' || v === 'right' ? v : 'right';
                    })();
                    const alignBulletsLeft = !!clientAIConfig?.pageSmartSummaryAlignBulletsLeft;
                    details.smartSummary = {
                        header,
                        bullets: smartSummary?.bullets || [],
                        generatedByAiLabel,
                        generatedByAiLabelAlignment,
                        alignBulletsLeft,
                        styles,
                        customCss,
                    };
                }
                if (cancelled) {
                    return;
                }
                window.parent.postMessage({
                    source: 'microcommerce-ai-embed',
                    type: 'pageContentReady',
                    details,
                }, '*');
                dispatchedRef.current = true;
            };
            dispatchPageContent();
            return () => {
                cancelled = true;
            };
        }
        if (!hasPrompts && !hasSummary) {
            dispatchedRef.current = false;
        }
        return undefined;
    }, [isLoading, suggestedPrompts, smartSummary, store, tokens]);
    return null;
};
export default observer(PageContentDispatcher);
//# sourceMappingURL=PageContentDispatcher.js.map